"use client";

import { useState, FormEvent } from "react";
import { toast } from "sonner";
import { joinWaitingList } from "@/app/vehicle/actions";

interface WaitingListFormProps {
  slug: string;
}

export default function WaitingListForm({ slug }: WaitingListFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailError, setEmailError] = useState(false);

  if (isSubmitted) {
    return (
      <p className="mt-3 text-sm text-amber-800">
        <i className="bootstrap-check-circle mr-1"></i>
        You&apos;re on the waiting list! We&apos;ll email you if this vehicle becomes available.
      </p>
    );
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = (formData.get("email") as string)?.trim();

    if (!email) {
      setEmailError(true);
      toast.error("Please enter your email address");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await joinWaitingList(slug, email);

      if (result.success) {
        toast.success("You've been added to the waiting list!");
        setIsSubmitted(true);
      } else {
        if (result.errors?.email) {
          setEmailError(true);
          toast.error(result.errors.email[0]);
        } else {
          toast.error(result.message || "Something went wrong. Please try again.");
        }
        setIsSubmitting(false);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 flex gap-2">
      <input
        type="email"
        name="email"
        placeholder="your@email.com"
        className={`flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
          emailError ? "border-red-500 bg-red-50" : "border-amber-300 bg-white"
        }`}
        onFocus={() => setEmailError(false)}
      />
      <button
        type="submit"
        disabled={isSubmitting}
        className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
      >
        {isSubmitting ? "Joining..." : "Join Waiting List"}
      </button>
    </form>
  );
}
